import { useCallback, useEffect, useRef, useState } from "react";
import { aiEditPrompts } from "../../prompts";
import { AppError, ErrorCategory, ErrorSeverity, handleError } from "../../utils/errorHandler";
import { featureValidators } from "../../utils/inputValidator";
import { markdownToHtml } from "../../utils/markdownToHtml";
import { callOpenAIStream } from "../../utils/openaiHelper";
import { replaceTextInOoxml } from "../../utils/ooxmlHelper";
import { useRequestManager } from "../../utils/requestManager";
import { trackEvent, trackTiming } from "../../utils/telemetry";
import {
	applyWithTrackedChanges,
	isTrackChangesSupported,
} from "../../utils/trackChangesHelper";
import { safeSync } from "../../utils/wordApiHelper";
import { copyToClipboard } from "../../utils/clipboardHelper";
import { ErrorDisplay } from "../shared/components";
import type { InlineDiffViewHandle } from "../shared/components";
import {
	MAX_EDITABLE_WORDS,
	MAX_HISTORY,
} from "./constants";
import {
	ComparisonPanel,
	EditFormPanel,
	NoSelectionState,
	SelectionTooLargeState,
} from "./components";
import type {
	ChangeExplanation,
	ComparisonState,
	DocumentMetadata,
	EditIntensity,
	HistoryEntry,
	ViewMode,
} from "./types";
import { useCustomPresets } from "./useCustomPresets";
import {
	BOOKMARK_NAME,
	cleanupBookmark,
	getBookmarkedRange,
} from "./utils/bookmarkUtils";
import { buildEditPromptContent } from "./utils/promptUtils";
import { captureSelectionForEdit } from "./utils/selectionUtils";
import { parseSuggestionsFromResponse } from "./utils/suggestionUtils";

/** Extracts the content text being built inside the "content" JSON field while streaming. */
const extractStreamingContent = (partialJson: string): string => {
	const matches = [...partialJson.matchAll(/"content"\s*:\s*"((?:[^"\\]|\\.)*)(?:"|$)/gs)];
	if (matches.length === 0) return '';
	const last = matches[matches.length - 1];
	return last[1]
		.replace(/\\n/g, '\n')
		.replace(/\\r/g, '')
		.replace(/\\t/g, '\t')
		.replace(/\\"/g, '"')
		.replace(/\\\\/g, '\\');
};

const AIEdit = () => {
	const [prompt, setPrompt] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<AppError | null>(null);
	const [viewMode, setViewMode] = useState<ViewMode>("diff");
	const [selectedTone, setSelectedTone] = useState("Professional");
	const [editIntensity, setEditIntensity] =
		useState<EditIntensity>("medium");
	const [selectedStyleGuide] = useState("");
	const [hasSelection, setHasSelection] = useState(false);
	const [selectedText, setSelectedText] = useState("");
	const [wordCount, setWordCount] = useState(0);
	const [streamingText, setStreamingText] = useState("");
	const [sessionHistory, setSessionHistory] = useState<HistoryEntry[]>([]);
	const [historyIndex, setHistoryIndex] = useState(-1);
	const [isSavingPreset, setIsSavingPreset] = useState(false);
	const [comparisonAction, setComparisonAction] = useState<'apply' | 'discard' | null>(null);
	const [presetName, setPresetName] = useState("");
	const [comparison, setComparison] = useState<ComparisonState>({
		original: "",
		suggestions: [],
		selectedSuggestionIndex: 0,
		isVisible: false,
	});
	const [originalOoxml, setOriginalOoxml] = useState<string | null>(null);
	const [useTrackChanges, setUseTrackChanges] = useState(false);
	const [applyStatusMessage, setApplyStatusMessage] = useState<{ text: string; isWarning: boolean } | null>(null);

	const [metadata, setMetadata] = useState<DocumentMetadata>({ matterRef: '', docDescription: '' });
	const [insertReviewComment, setInsertReviewComment] = useState(false);

	const diffRef = useRef<InlineDiffViewHandle>(null);
	const explanationCache = useRef<Map<string, ChangeExplanation[]>>(new Map());
	const requestManager = useRequestManager();
	const { presets: customPresets, addPreset, deletePreset, isAtLimit, maxPresets } = useCustomPresets();

	const loadHistoryEntry = useCallback(
		(index: number) => {
			if (index < 0 || index >= sessionHistory.length) return;
			const entry = sessionHistory[index];
			setHistoryIndex(index);
			setComparison({
				original: entry.original,
				suggestions: entry.suggestions,
				selectedSuggestionIndex: 0,
				isVisible: true,
			});
		},
		[sessionHistory]
	);

	const getImprovedText = async (promptOverride?: string) => {
		if (!requestManager.canMakeRequest()) {
			console.warn("[AIEdit] Request already in progress, ignoring duplicate request");
			return;
		}

		requestManager.startRequest();
		setIsLoading(true);
		setError(null);
		setStreamingText("");
		setOriginalOoxml(null);
		const startTime = Date.now();

		try {
			const { originalText, surroundingContext, originalOoxml: capturedOoxml } =
				await Word.run(async (context) => captureSelectionForEdit(context));
			if (capturedOoxml) setOriginalOoxml(capturedOoxml);

			const activePrompt = promptOverride || prompt;
			const validation = featureValidators.aiEdit(originalText, activePrompt);
			if (!validation.isValid) throw new Error(validation.error);

			const userPromptContent = buildEditPromptContent({
				originalText,
				activePrompt,
				selectedTone,
				selectedStyleGuide,
				surroundingContext,
				editIntensity,
			});

			let fullResponse = "";
			const stream = callOpenAIStream({
				signal: requestManager.getSignal(),
				response_format: { type: "json_object" },
				messages: [
					{ role: "system", content: aiEditPrompts.systemPrompt },
					{ role: "user", content: userPromptContent },
				],
				onRetry: (attempt, maxRetries) => {
					fullResponse = "";
					setStreamingText(`Retrying\u2026 (attempt ${attempt} of ${maxRetries})`);
				},
			});

			for await (const chunk of stream) {
				fullResponse += chunk;
				setStreamingText(extractStreamingContent(fullResponse));
			}

			const suggestions = parseSuggestionsFromResponse(fullResponse);
			setStreamingText("");

			trackTiming("ai-edit", "generate", startTime, {
				suggestionCount: suggestions.length,
			});
			trackEvent("feature_usage", "ai-edit", "generate", {
				tone: selectedTone || "default",
				intensity: editIntensity,
				styleGuide: selectedStyleGuide || "none",
				promptLength: activePrompt.length,
				originalLength: originalText.length,
			});

			setSessionHistory((previous) => {
				const entry: HistoryEntry = {
					prompt: activePrompt,
					original: originalText,
					suggestions,
					timestamp: Date.now(),
				};
				return [entry, ...previous].slice(0, MAX_HISTORY);
			});
			setHistoryIndex(0);

			setComparison({
				original: originalText,
				suggestions,
				selectedSuggestionIndex: 0,
				isVisible: true,
			});
		} catch (err) {
			// Clean up the orphaned bookmark first — abort is an exit path too, and
			// captureSelectionForEdit has already dropped the bookmark by this point.
			await cleanupBookmark();
			if (requestManager.isAborted(err)) return;
			const appError = handleError(err, "AIEdit", {
				promptLength: prompt.length,
				operation: "getImprovedText",
			});
			setError(appError);
		} finally {
			requestManager.endRequest();
			setIsLoading(false);
			setStreamingText("");
		}
	};

	const showApplyStatus = (text: string, isWarning: boolean) => {
		setApplyStatusMessage({ text, isWarning });
		setTimeout(() => setApplyStatusMessage(null), 5000);
	};

	const resolveRangeFontName = async (
		context: Word.RequestContext,
		range: Word.Range,
		ooxml: string | null
	): Promise<string | null> => {
		range.font.load("name");
		await safeSync(context, "load target font");

		if (typeof range.font.name === "string") {
			const trimmedFontName = range.font.name.trim();
			if (trimmedFontName && trimmedFontName !== "Mixed") {
				return trimmedFontName;
			}
		}

		try {
			const textRanges = range.getTextRanges([" ", "\t", "\n", "\r"], true);
			textRanges.load("items/font/name");
			await safeSync(context, "load text range fonts");

			for (const textRange of textRanges.items) {
				const fontName = typeof textRange.font.name === "string" ? textRange.font.name.trim() : "";
				if (fontName && fontName !== "Mixed") {
					return fontName;
				}
			}
		} catch (error) {
			console.warn("[AIEdit] Could not resolve font from text ranges:", error);
		}

		const ooxmlFontMatch = ooxml?.match(/<w:rFonts\b[^>]*w:(?:ascii|hAnsi|cs|eastAsia)=\"([^\"]+)\"/i);
		if (ooxmlFontMatch?.[1]) {
			return ooxmlFontMatch[1];
		}

		return null;
	};

	const applyChanges = async () => {
		// Guard here, not inside Word.run: returning from that callback would skip the
		// bookmark delete and still report success over an untouched document.
		const selectedSuggestion =
			comparison.suggestions[comparison.selectedSuggestionIndex];
		if (!selectedSuggestion) {
			showApplyStatus("No suggestion selected — nothing to apply.", true);
			return;
		}

		setComparisonAction('apply');
		let appliedAsPlainText = false;
		let commentInserted = false;
		try {
			await Word.run(async (context) => {
				const targetRange = await getBookmarkedRange(context);

				let originalFontName: string | null = null;
				const preserveFont = async (range: Word.Range) => {
					if (!originalFontName) return;
					await safeSync(context, "insert replacement");
					range.font.name = originalFontName;
					targetRange.font.name = originalFontName;
					await safeSync(context, "preserve font");
				};
				if (!insertReviewComment) {
					originalFontName = await resolveRangeFontName(context, targetRange, originalOoxml);
				}

				if (insertReviewComment) {
					// "Add Comment" mode: non-destructive — attach suggestion as a Word
					// comment anchored to the original text. Document is NOT modified.
					if (typeof (targetRange as { insertComment?: unknown }).insertComment !== 'function') {
						throw new Error('Comment insertion requires Word API 1.4 or later. Please update Microsoft Word.');
					}
					// @ts-expect-error insertComment is Word API 1.4, not yet in @types/office-js
					targetRange.insertComment(selectedSuggestion.content);
					commentInserted = true;
				} else if (viewMode === "diff" && diffRef.current) {
					const composedText = diffRef.current.getComposedText();
					if (useTrackChanges) {
						const trackedSuccess = await applyWithTrackedChanges(
							context,
							targetRange,
							composedText,
							"text",
							originalFontName
						);
						if (!trackedSuccess) {
							const insertedRange = targetRange.insertText(composedText, Word.InsertLocation.replace);
							await preserveFont(insertedRange);
							appliedAsPlainText = true;
						}
					} else if (originalOoxml) {
						const modifiedOoxml = replaceTextInOoxml(originalOoxml, composedText, originalFontName);
						if (modifiedOoxml) {
							// Queue the OOXML insertion — no sync yet
							const insertedRange = targetRange.insertOoxml(modifiedOoxml, Word.InsertLocation.replace);
							await preserveFont(insertedRange);
						} else {
							const insertedRange = targetRange.insertText(composedText, Word.InsertLocation.replace);
							await preserveFont(insertedRange);
							appliedAsPlainText = true;
						}
					} else {
						const insertedRange = targetRange.insertText(composedText, Word.InsertLocation.replace);
						await preserveFont(insertedRange);
					}
				} else {
					const html = markdownToHtml(selectedSuggestion.content);
					if (useTrackChanges) {
						const trackedSuccess = await applyWithTrackedChanges(
							context,
							targetRange,
							html,
							"html",
							originalFontName
						);
						if (!trackedSuccess) {
							const insertedRange = targetRange.insertHtml(html, Word.InsertLocation.replace);
							await preserveFont(insertedRange);
							appliedAsPlainText = true;
						}
					} else {
						const insertedRange = targetRange.insertHtml(html, Word.InsertLocation.replace);
						await preserveFont(insertedRange);
					}
				}

				// Batch: commit the queued insertion AND delete the bookmark in a single
				// round-trip — avoids the previous redundant safeSync + cleanupBookmarkInContext
				// pattern that sent 2-3 extra syncs for every apply.
				context.document.deleteBookmark(BOOKMARK_NAME);
				await safeSync(context, "apply changes");
				setComparison((previous) => ({ ...previous, isVisible: false }));
			});

			trackEvent("user_action", "ai-edit", "apply", {
				viewMode,
				useTrackChanges,
				usedOoxml: !!originalOoxml,
				appliedAsPlainText,
				insertedComment: commentInserted,
			});

			showApplyStatus(
				commentInserted
					? "Suggestion added as comment."
					: appliedAsPlainText
						? "Applied as plain text — complex formatting could not be preserved."
						: "Changes applied successfully.",
				appliedAsPlainText
			);
		} catch (err) {
			const appError = handleError(err, "AIEdit", { operation: "applyChanges" });
			setError(appError);
			// Ensure bookmark is cleaned up even when the apply failed
			await cleanupBookmark();
		} finally {
			setComparisonAction(null);
		}
	};

	const discardChanges = async () => {
		setComparisonAction('discard');
		try {
			setComparison((previous) => ({ ...previous, isVisible: false }));
			await cleanupBookmark();
			trackEvent("user_action", "ai-edit", "discard");
		} finally {
			setComparisonAction(null);
		}
	};

	const modifyChanges = async () => {
		setComparison((previous) => ({ ...previous, isVisible: false }));
		await cleanupBookmark();
	};

	const handleQuickAction = (actionPrompt: string) => {
		setPrompt(actionPrompt);
		trackEvent("user_action", "ai-edit", "quick_action", {
			prompt: actionPrompt.substring(0, 50),
		});
	};

	const handleSavePreset = () => {
		if (!prompt.trim()) return;
		setIsSavingPreset(true);
		setPresetName("");
	};

	const submitSavePreset = () => {
		if (!presetName.trim()) {
			setIsSavingPreset(false);
			return;
		}
		if (isAtLimit()) {
			setError({
				message: `Preset limit reached`,
				userMessage: `You have reached the maximum of ${maxPresets} presets. Please delete an existing preset before adding a new one.`,
				category: ErrorCategory.VALIDATION,
				severity: ErrorSeverity.LOW,
				timestamp: new Date().toISOString(),
			});
			setIsSavingPreset(false);
			return;
		}
		const saved = addPreset(presetName, prompt);
		if (!saved) {
			setError({
				message: `Failed to save preset`,
				userMessage: `Could not save the preset. Your browser storage may be full.`,
				category: ErrorCategory.VALIDATION,
				severity: ErrorSeverity.LOW,
				timestamp: new Date().toISOString(),
			});
		} else {
			trackEvent("user_action", "ai-edit", "save_preset", { name: presetName });
		}
		setIsSavingPreset(false);
	};

	const cancelSavePreset = () => {
		setIsSavingPreset(false);
		setPresetName("");
	};

	const handleCopyReport = async () => {
		const entry = sessionHistory[historyIndex];
		if (!entry) return;
		const suggestion = comparison.suggestions[comparison.selectedSuggestionIndex];

		const lines = [
			"=== AI Edit Review Report ===",
			`Date:       ${new Date(entry.timestamp).toLocaleString()}`,
			...(metadata.matterRef.trim()       ? [`Matter/Ref: ${metadata.matterRef.trim()}`]       : []),
			...(metadata.docDescription.trim()  ? [`Document:   ${metadata.docDescription.trim()}`]  : []),
			"", "--- Prompt ---",    entry.prompt,
			"", "--- Original ---",  entry.original,
			"", "--- Final Text ---", suggestion?.content ?? "",
		];

		const result = await copyToClipboard(lines.join('\n'), { featureName: 'AIEdit-Report' });
		showApplyStatus(
			result.success ? "Review report copied to clipboard." : (result.error ?? "Could not copy report."),
			!result.success
		);
	};

	useEffect(() => {
		const handleSelectionChanged = async () => {
			if (requestManager.isLoading()) return;
			try {
				await Word.run(async (context) => {
					const selection = context.document.getSelection();
					context.load(selection, "text");
					await context.sync();

					const text = selection.text.trim();
					setHasSelection(text.length > 0);

					const words = text ? text.split(/\s+/).filter(Boolean) : [];
					setWordCount(words.length);

					if (text.length > 0) setSelectedText(text);
				});
			} catch {
				// Silently ignore Word event polling errors.
			}
		};

		handleSelectionChanged();

		let intervalId: ReturnType<typeof setInterval> | null = null;
		try {
			Office.context.document.addHandlerAsync(
				Office.EventType.DocumentSelectionChanged,
				handleSelectionChanged
			);
		} catch {
			intervalId = setInterval(handleSelectionChanged, 1000);
		}

		return () => {
			requestManager.cleanup();
			if (intervalId) {
				clearInterval(intervalId);
			} else {
				try {
					Office.context.document.removeHandlerAsync(
						Office.EventType.DocumentSelectionChanged,
						{ handler: handleSelectionChanged }
					);
				} catch {
					// Ignore cleanup errors.
				}
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (
				event.key === "Enter" &&
				(event.ctrlKey || event.metaKey) &&
				!isLoading &&
				hasSelection
			) {
				event.preventDefault();
				getImprovedText();
			}

			if (event.key === "Escape" && comparison.isVisible) {
				event.preventDefault();
				discardChanges();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	});

	return (
		<div className="bg-white rounded-lg">
			<div className="space-y-4">
				{applyStatusMessage && (
					<div
						className={`mx-4 mt-3 px-3 py-2.5 rounded-xl text-xs flex items-start gap-2 ${
							applyStatusMessage.isWarning
								? "bg-amber-50 border border-amber-200 text-amber-800"
								: "bg-green-50 border border-green-200 text-green-800"
						}`}
						role="status"
						aria-live="polite"
					>
						{applyStatusMessage.isWarning ? (
							<svg className="flex-shrink-0 mt-0.5" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
						) : (
							<svg className="flex-shrink-0 mt-0.5" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
						)}
						<span className="flex-1 leading-relaxed">{applyStatusMessage.text}</span>
						<button
							onClick={() => setApplyStatusMessage(null)}
							className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
							aria-label="Dismiss"
						>
							<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
						</button>
					</div>
				)}

				{error && (
					<div className="p-4">
						<ErrorDisplay
							error={error}
							onDismiss={() => setError(null)}
							onRetry={() => getImprovedText()}
						/>
					</div>
				)}

				{!comparison.isVisible && (
					<div className="p-4">
						{!hasSelection ? (
							<NoSelectionState />
						) : wordCount > MAX_EDITABLE_WORDS ? (
							<SelectionTooLargeState wordCount={wordCount} />
						) : (
							<EditFormPanel
								selectedText={selectedText}
								wordCount={wordCount}
								isLoading={isLoading}
								prompt={prompt}
								selectedTone={selectedTone}
								editIntensity={editIntensity}
								customPresets={customPresets}
								isSavingPreset={isSavingPreset}
								presetName={presetName}
								streamingText={streamingText}
								metadata={metadata}
								onMetadataChange={setMetadata}
								onQuickAction={handleQuickAction}
								onToneChange={setSelectedTone}
								onEditIntensityChange={setEditIntensity}
								onPromptChange={setPrompt}
								onStartSavePreset={handleSavePreset}
								onPresetNameChange={setPresetName}
								onSubmitSavePreset={submitSavePreset}
								onCancelSavePreset={cancelSavePreset}
								onDeletePreset={deletePreset}
								onGenerate={() => getImprovedText()}
								onAbort={() => requestManager.abort()}
							/>
						)}
					</div>
				)}

				{comparison.isVisible && (
					<ComparisonPanel
						prompt={prompt}
						comparisonAction={comparisonAction}
						comparison={comparison}
						sessionHistory={sessionHistory}
						historyIndex={historyIndex}
						viewMode={viewMode}
						useTrackChanges={useTrackChanges}
						trackChangesSupported={isTrackChangesSupported()}
						diffRef={diffRef}
						explanationCache={explanationCache}
						insertReviewComment={insertReviewComment}
						onModify={modifyChanges}
						onLoadHistoryEntry={loadHistoryEntry}
						onViewModeChange={setViewMode}
						onSelectedSuggestionChange={(selectedSuggestionIndex) =>
							setComparison((previous) => ({
								...previous,
								selectedSuggestionIndex,
							}))
						}
						onUseTrackChangesChange={(checked) => {
							setUseTrackChanges(checked);
							if (checked) setInsertReviewComment(false);
						}}
						onInsertReviewCommentChange={(checked) => {
							setInsertReviewComment(checked);
							if (checked) setUseTrackChanges(false);
						}}
						onCopyReport={handleCopyReport}
						onDiscard={discardChanges}
						onApply={applyChanges}
					/>
				)}
			</div>
		</div>
	);
};

export default AIEdit;
